import type { IntlShape } from "@formatjs/intl";
import { Space, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMemo } from "react";
import { formatHours } from "../../common/utils/formatHours.ts";
import type { TechnicianReportRow } from "../generated-axios-client";

type HoursField =
  | "check_in_hours"
  | "standby_hours"
  | "estimated_task_hours"
  | "logged_task_hours";

const hoursColumn = (
  intl: IntlShape,
  field: HoursField,
  titleId: string
): ColumnsType<TechnicianReportRow>[number] => ({
  key: field,
  title: intl.formatMessage({ id: titleId }),
  align: "right",
  dataIndex: field,
  render: (hours: number) => formatHours(hours),
  sorter: (first, second) => first[field] - second[field],
});

const getTechnicianColumns = (intl: IntlShape): ColumnsType<TechnicianReportRow> => [
  {
    key: "name",
    title: intl.formatMessage({ id: "work_order_totals_column_name" }),
    dataIndex: "name",
    fixed: "left",
    sorter: (first, second) => first.name.localeCompare(second.name),
  },
  {
    key: "completed_work_order_count",
    title: intl.formatMessage({ id: "technicians_report_column_completed_work_orders" }),
    align: "right",
    dataIndex: "completed_work_order_count",
    sorter: (first, second) =>
      first.completed_work_order_count - second.completed_work_order_count,
  },
  {
    key: "active_work_order_count",
    title: intl.formatMessage({ id: "technicians_report_column_active_work_orders" }),
    align: "right",
    dataIndex: "active_work_order_count",
    sorter: (first, second) =>
      first.active_work_order_count - second.active_work_order_count,
  },
  {
    key: "completed_task_count",
    title: intl.formatMessage({ id: "technicians_report_column_completed_tasks" }),
    align: "right",
    dataIndex: "completed_task_count",
    sorter: (first, second) => first.completed_task_count - second.completed_task_count,
  },
  {
    key: "average_rating",
    title: intl.formatMessage({ id: "technicians_report_column_average_evaluation" }),
    align: "right",
    render: (_, row) =>
      row.average_rating === null ? (
        "—"
      ) : (
        <Space size="small">
          {`${row.average_rating.toFixed(1)} ★`}
          <Typography.Text type="secondary">
            {intl.formatMessage(
              { id: "technicians_report_rated_count" },
              { count: row.rated_work_order_count }
            )}
          </Typography.Text>
        </Space>
      ),
    sorter: (first, second) => (first.average_rating ?? 0) - (second.average_rating ?? 0),
  },
  hoursColumn(intl, "check_in_hours", "work_order_totals_stat_check_in_time"),
  hoursColumn(intl, "standby_hours", "work_order_totals_stat_standby_time"),
  hoursColumn(intl, "estimated_task_hours", "work_order_totals_stat_estimated_task_time"),
  hoursColumn(intl, "logged_task_hours", "work_order_totals_stat_logged_task_time"),
];

type TechniciansReportTableProps = {
  intl: IntlShape;
  rows: TechnicianReportRow[] | undefined;
  loading: boolean;
};

export const TechniciansReportTable = ({
  intl,
  rows,
  loading,
}: TechniciansReportTableProps) => {
  const technicianColumns = useMemo(() => getTechnicianColumns(intl), [intl]);

  return (
    <Table<TechnicianReportRow>
      size="small"
      rowKey="user_id"
      columns={technicianColumns}
      dataSource={rows ?? []}
      loading={loading}
      pagination={false}
      scroll={{ x: "max-content" }}
      locale={{
        emptyText: intl.formatMessage({ id: "technicians_report_no_technicians" }),
      }}
    />
  );
};
